/**
 * Wall-clock based game clock + shot clock (§4.4).
 *
 * Nothing counts down with a timer. While running we store `runningSince` (wall ms) and the
 * values at that instant; the display value is `remainingAtStart − (now − runningSince)`.
 * The state is plain data, so it survives a reload when rebuilt from events.
 *
 * The shot clock shares the game clock's run/stop state (one operator, one toggle).
 * Both stop advancing once the game clock reaches 0.
 */
import type { RuleSet, ShotClockRule } from '../rules/ruleset';
import { periodLengthMs } from '../rules/ruleset';

export interface ClockState {
  /** Wall-clock ms when the clock last started (or was re-anchored); null when stopped. */
  runningSince: number | null;
  /** Game-clock remaining (ms) at `runningSince`, or the current value when stopped. */
  remainingMsAtStart: number;
  shotEnabled: boolean;
  /** Shot-clock remaining (ms) at `runningSince`, or the current value when stopped or held. */
  shotRemainingMsAtStart: number;
  /** The shot clock waits (after a basket) until the throw-in is touched. */
  shotHeld: boolean;
}

export function createClock(gameMs: number, shotClock: ShotClockRule): ClockState {
  return {
    runningSince: null,
    remainingMsAtStart: Math.max(0, gameMs),
    shotEnabled: shotClock.enabled,
    shotRemainingMsAtStart: shotClock.enabled ? shotClock.fullSec * 1000 : 0,
    shotHeld: false,
  };
}

export function isRunning(clock: ClockState): boolean {
  return clock.runningSince !== null;
}

/** Game time that has actually run since `runningSince` (capped at the remaining time). */
function runElapsed(clock: ClockState, now: number): number {
  if (clock.runningSince === null) return 0;
  const elapsed = Math.max(0, now - clock.runningSince);
  return Math.min(elapsed, clock.remainingMsAtStart);
}

/** Game-clock remaining in ms at wall time `now` (never below 0). */
export function remaining(clock: ClockState, now: number): number {
  return Math.max(0, clock.remainingMsAtStart - runElapsed(clock, now));
}

/** Shot-clock remaining in ms at `now` (never below 0). 0 when disabled. */
export function shotRemaining(clock: ClockState, now: number): number {
  if (!clock.shotEnabled) return 0;
  if (clock.shotHeld) return clock.shotRemainingMsAtStart;
  return Math.max(0, clock.shotRemainingMsAtStart - runElapsed(clock, now));
}

/** True once the game clock has reached 0:00. */
export function isExpired(clock: ClockState, now: number): boolean {
  return remaining(clock, now) === 0;
}

/**
 * The shot clock is shown/active only when enabled and the game clock is not below it
 * (it is switched off when game clock < shot clock).
 */
export function shotActive(clock: ClockState, now: number): boolean {
  if (!clock.shotEnabled) return false;
  return remaining(clock, now) >= shotRemaining(clock, now);
}

/** Start the clock at `now` (play resumes, so a held shot clock starts too). No-op if running. */
export function start(clock: ClockState, now: number): ClockState {
  if (clock.runningSince !== null) return clock;
  return { ...clock, runningSince: now, shotHeld: false };
}

/**
 * Stop the clock at `now`, freezing both clocks. No-op if already stopped.
 * `authoritativeGameMs` (e.g. the stored clockMs of a CLOCK_STOPPED event) overrides the
 * computed game value when given.
 */
export function stop(clock: ClockState, now: number, authoritativeGameMs?: number): ClockState {
  if (clock.runningSince === null) {
    return authoritativeGameMs === undefined
      ? clock
      : { ...clock, remainingMsAtStart: Math.max(0, authoritativeGameMs) };
  }
  return {
    ...clock,
    runningSince: null,
    remainingMsAtStart:
      authoritativeGameMs === undefined ? remaining(clock, now) : Math.max(0, authoritativeGameMs),
    shotRemainingMsAtStart: shotRemaining(clock, now),
  };
}

/** Set the game clock to `newRemainingMs` at `now`, keeping the run state and shot clock. */
export function adjust(clock: ClockState, newRemainingMs: number, now: number): ClockState {
  const shot = shotRemaining(clock, now);
  return {
    ...clock,
    runningSince: clock.runningSince === null ? null : now,
    remainingMsAtStart: Math.max(0, newRemainingMs),
    shotRemainingMsAtStart: shot,
  };
}

/**
 * Reset the shot clock to `seconds` at `now`; with `hold` it waits there. No-op when the shot
 * clock is disabled.
 */
export function resetShot(
  clock: ClockState,
  seconds: number,
  now: number,
  hold = false,
): ClockState {
  if (!clock.shotEnabled) return clock;
  const ms = Math.max(0, seconds * 1000);
  if (clock.runningSince === null || hold) {
    return { ...clock, shotRemainingMsAtStart: ms, shotHeld: hold };
  }
  // Re-anchor so that shotRemaining(now) === ms while keeping the shared runningSince.
  return { ...clock, shotRemainingMsAtStart: ms + runElapsed(clock, now), shotHeld: false };
}

/** The throw-in was touched: a held shot clock starts counting at `now`. */
export function releaseShot(clock: ClockState, now: number): ClockState {
  if (!clock.shotHeld) return clock;
  return {
    ...clock,
    shotHeld: false,
    shotRemainingMsAtStart: clock.shotRemainingMsAtStart + runElapsed(clock, now),
  };
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/**
 * Game-clock display. `mm:ss` from one minute up, `s.t` (tenths) under one minute.
 * Values are rounded UP (as on real scoreboards), so 0.0 / 00:00 is shown only when the
 * time has fully expired, and a fresh 10-minute period shows 10:00.
 * Examples: 600000 → "10:00", 599000 → "09:59", 60000 → "01:00", 59900 → "59.9",
 * 5250 → "5.3", 0 → "0.0".
 */
export function formatGameClock(ms: number): string {
  const v = Math.max(0, Math.round(ms));
  if (v < 60000) {
    const tenths = Math.ceil(v / 100);
    if (tenths < 600) return `${Math.floor(tenths / 10)}.${tenths % 10}`;
  }
  const totalSec = Math.ceil(v / 1000);
  return `${pad2(Math.floor(totalSec / 60))}:${pad2(totalSec % 60)}`;
}

/** Shot-clock display in whole seconds, rounded up ("24" … "1", "0" at expiry). */
export function formatShotClock(ms: number): string {
  return String(Math.ceil(Math.max(0, Math.round(ms)) / 1000));
}

/** Game time elapsed (ms) from the start of period 1 to `remainingMs` in `periodNo`. */
export function elapsedGameMs(rules: RuleSet, periodNo: number, remainingMs: number): number {
  if (periodNo < 1) return 0;
  let total = 0;
  for (let p = 1; p < periodNo; p++) total += periodLengthMs(rules, p);
  const len = periodLengthMs(rules, periodNo);
  const rem = Math.min(len, Math.max(0, remainingMs));
  return total + (len - rem);
}

/** Total scheduled game time (ms) through the end of `periodNo` (default: regulation). */
export function scheduledGameMs(rules: RuleSet, throughPeriod: number = rules.periods): number {
  let total = 0;
  for (let p = 1; p <= throughPeriod; p++) total += periodLengthMs(rules, p);
  return total;
}
